

import React, { useState, useRef, useEffect } from "react";
import { Upload, FileText, AlertCircle, X, Check } from "lucide-react";
import { useToast } from "../context/ToastContext"; // ✅ Use real toast context

const FileUploadModal = ({ isOpen, onClose, onUploadSuccess, onUploadFailure }) => {
    const [file, setFile] = useState(null);
    const [error, setError] = useState("");
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const fileInputRef = useRef(null);
    const { showToast } = useToast(); // ✅ Using shared toast

    useEffect(() => {
        if (!isOpen) {
            setFile(null);
            setError("");
            setIsUploading(false);
            setIsDragOver(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    }, [isOpen]);

    const processFile = (selectedFile) => {
        const allowedTypes = [
            "application/pdf",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        ];

        if (selectedFile && !allowedTypes.includes(selectedFile.type)) {
            setError("Only PDF, DOC, and DOCX files are accepted.");
            setFile(null);
            return;
        }

        if (selectedFile && selectedFile.size > 5 * 1024 * 1024) {
            setError("File size must be under 5 MB.");
            setFile(null);
            return;
        }

        setError("");
        setFile(selectedFile);
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        processFile(selectedFile);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const droppedFile = e.dataTransfer.files[0];
        processFile(droppedFile);
    };

    const handleUpload = async () => {
        if (!file) {
            setError("Please select a file first.");
            return;
        }

        // Debug: Check if API key is available at build time
        console.log("Sending API Key:", process.env.REACT_APP_API_KEY || "❌ MISSING");
        if (!process.env.REACT_APP_API_KEY) {
            showToast("API key is missing. Check your .env file.", "error");
            return;
        }

        setIsUploading(true);
        const formData = new FormData();
        formData.append("file", file);

        // Manual timeout for better browser compatibility
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 seconds

        try {
            const response = await fetch("https://navam-web-server.onrender.com/uploads", {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${process.env.REACT_APP_API_KEY}`,
                },
                body: formData,
                signal: controller.signal,
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                // Handle both JSON and plain-text errors
                const contentType = response.headers.get("content-type") || "";
                let errorMessage;
                if (contentType.includes("application/json")) {
                    const errorData = await response.json();
                    errorMessage = errorData.error || "Upload failed";
                } else {
                    errorMessage = await response.text();
                }
                showToast(errorMessage, "error");
                if (onUploadFailure) onUploadFailure(errorMessage);
                onClose();
                return;
            }

            const data = await response.json();
            showToast("Resume uploaded successfully! We'll review your application soon.", "success");
            if (onUploadSuccess) onUploadSuccess(data.url);
            onClose();
        } catch (error) {
            console.error("Error uploading file:", error);
            const message =
                error.name === "AbortError"
                    ? "Upload timed out. Please try again."
                    : "Upload failed. Please check your connection and try again.";
            showToast(message, "error");
            if (onUploadFailure) onUploadFailure(message);
            onClose();
        } finally {
            clearTimeout(timeoutId);
            setIsUploading(false);
        }
    };


    const handleCancel = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl w-full max-w-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0e6d87]/5 to-blue-500/5" />

                <div className="relative z-10 flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-r from-[#0e6d87] to-blue-600 rounded-full flex items-center justify-center mr-3">
                            <Upload className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-2xl font-bold text-gray-900">Upload Your Resume</h3>
                    </div>
                    <button
                        onClick={handleCancel}
                        className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors duration-200"
                    >
                        <X className="w-4 h-4 text-gray-600" />
                    </button>
                </div>

                <div className="relative z-10 p-6">
                    <p className="text-gray-600 text-center mb-6 leading-relaxed">
                        Share your professional journey with us. Upload your resume to explore exciting career opportunities.
                    </p>

                    <div
                        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 mb-4 ${isDragOver
                            ? 'border-[#0e6d87] bg-[#0e6d87]/5'
                            : error
                                ? 'border-red-300 bg-red-50/50'
                                : file
                                    ? 'border-green-300 bg-green-50/50'
                                    : 'border-gray-300 hover:border-[#0e6d87] hover:bg-[#0e6d87]/5'
                            }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        <div className="space-y-4">
                            {file ? (
                                <div className="flex items-center justify-center">
                                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mr-3">
                                        <FileText className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div className="text-left">
                                        <p className="font-semibold text-gray-900">{file.name}</p>
                                        <p className="text-sm text-gray-600">
                                            {(file.size / (1024 * 1024)).toFixed(2)} MB
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                        <Upload className="w-6 h-6 text-gray-400" />
                                    </div>
                                    <div>
                                        <p className="text-lg font-semibold text-gray-700 mb-2">
                                            Drop your resume here or click to browse
                                        </p>
                                        <p className="text-sm text-gray-500">
                                            PDF, DOC, DOCX • Max 5 MB
                                        </p>
                                    </div>
                                </>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            onChange={handleFileChange}
                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                            accept=".pdf,.doc,.docx"
                        />
                    </div>

                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                            <div className="flex items-center">
                                <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
                                <p className="text-red-600 font-medium">{error}</p>
                            </div>
                        </div>
                    )}

                    <div className="flex gap-3">
                        <button
                            onClick={handleCancel}
                            className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-xl transition-all duration-300"
                            disabled={isUploading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleUpload}
                            className={`flex-1 px-4 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center ${!file || isUploading
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gradient-to-r from-[#0e6d87] to-blue-600 hover:from-[#0b566a] hover:to-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105'
                                }`}
                            disabled={!file || isUploading}
                        >
                            {isUploading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Uploading...
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4 mr-2" />
                                    Submit Resume
                                </>
                            )}
                        </button>
                    </div>

                    <p className="text-xs text-gray-500 text-center mt-4 leading-relaxed">
                        By uploading, you agree to our{" "}
                        <a href="/terms" className="text-[#0e6d87] hover:underline font-medium">
                            Terms of Service
                        </a>{" "}
                        and{" "}
                        <a href="/privacy-policy" className="text-[#0e6d87] hover:underline font-medium">
                            Privacy Policy
                        </a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default FileUploadModal;
